import React, { useState } from 'react';
import { App as AntdApp, Empty, Spin, Row, Col, Input, Space, Button, Typography, Descriptions, Upload, theme } from 'antd';
import { withDevModalFilePath } from '../common';

const { Modal } = withDevModalFilePath('client/src/components/file/FileGallery.tsx');
import {
  DownloadOutlined,
  DeleteOutlined,
  LeftOutlined,
  RightOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  CameraOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { CameraCapture } from './CameraCapture';
import { compressImage } from '../../utils/imageCompression';
import { validateFile } from '../../utils/fileValidation';
import { useIsMobile } from '../../hooks/useMediaQuery';
import type { UploadProps } from 'antd';

const { Text } = Typography;

interface FileRecord {
  id: string;
  mimeType: string;
  originalName: string;
  size?: number;
  fileSize?: number;
  previewUrl?: string | null;
  createdAt?: string;
  uploaderName?: string | null;
}

interface FileGalleryProps {
  entityType?: string;
  entityId?: string;
  category?: string;
  editable?: boolean;
  onFileDeleted?: () => void;
  maxFiles?: number;
  maxSize?: number;
  accept?: string;
  enableCamera?: boolean;
}

export const FileGallery: React.FC<FileGalleryProps> = ({
  entityType,
  entityId,
  category,
  editable = false,
  onFileDeleted,
  maxFiles = 30,
  maxSize = 10,
  accept = 'image/*,video/*',
  enableCamera = true,
}) => {
  const { token } = theme.useToken();
  const { message: messageApi, modal: modalApi } = AntdApp.useApp();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useIsMobile();

  const { data, isLoading, refetch } = trpc.file.list.useQuery({
    entityType,
    entityId,
    category,
  });
  const files: FileRecord[] = Array.isArray(data?.items)
    ? data.items.filter(
        (item): item is FileRecord => Boolean(item?.id && item?.originalName && item?.mimeType)
      )
    : [];

  const trpcUtils = trpc.useUtils();
  const uploadViaServerMutation = trpc.file.uploadViaServer.useMutation();
  const deleteFileMutation = trpc.file.delete.useMutation({
    onSuccess: () => {
      messageApi.success('ファイルが削除されました');
      setPreviewVisible(false);
      refetch();
      onFileDeleted?.();
    },
    onError: (error: unknown) => {
      messageApi.error(`削除エラー: ${getErrorMessage(error, '詳細不明')}`);
    },
  });

  const handleUpload = async (file: File) => {
    if (!entityType || !entityId) {
      messageApi.error('エンティティ情報が不足しています');
      return;
    }

    // バリデーション
    const validation = validateFile(file, {
      maxSizeMB: maxSize,
      allowedTypes: accept.split(',').map((t) => t.trim()),
    });

    if (!validation.valid) {
      messageApi.error(validation.error);
      return;
    }

    try {
      messageApi.loading(`${file.name} をアップロード中...`, 0);

      // 画像の場合は圧縮
      let processedFile = file;
      if (file.type.startsWith('image/')) {
        try {
          processedFile = await compressImage(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
          });
        } catch (error) {
          console.warn('画像圧縮に失敗しました。元のファイルをアップロードします:', error);
        }
      }

      // ファイルをBase64に変換
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64String = result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(processedFile);
      });

      // サーバー経由でアップロード
      await uploadViaServerMutation.mutateAsync({
        fileName: processedFile.name,
        fileType: processedFile.type,
        fileSize: processedFile.size,
        fileData: base64Data,
        category,
        associations: [{ entityType, entityId }],
      });

      messageApi.destroy();
      messageApi.success(`${file.name} がアップロードされました`);
      refetch();
    } catch (error: unknown) {
      messageApi.destroy();
      messageApi.error(`${file.name} のアップロードに失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    }
  };

  const getFileIcon = (mimeType: string | null, _fileName: string) => {
    const iconStyle = { fontSize: 64, color: token.colorTextTertiary };

    if (!mimeType) {
      return <FileOutlined style={iconStyle} />;
    }

    if (mimeType.startsWith('image/')) {
      return <FileImageOutlined style={{ ...iconStyle, color: token.colorSuccess }} />;
    }
    if (mimeType === 'application/pdf') {
      return <FilePdfOutlined style={{ ...iconStyle, color: token.colorError }} />;
    }
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
      return <FileExcelOutlined style={{ ...iconStyle, color: token.colorSuccess }} />;
    }
    if (mimeType.includes('word') || mimeType.includes('document')) {
      return <FileWordOutlined style={{ ...iconStyle, color: token.colorPrimary }} />;
    }
    if (mimeType.startsWith('text/')) {
      return <FileTextOutlined style={iconStyle} />;
    }
    return <FileOutlined style={iconStyle} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes <= 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const resolveFileSize = (file: FileRecord): number => {
    if (typeof file.fileSize === 'number') {
      return file.fileSize;
    }
    if (typeof file.size === 'number') {
      return file.size;
    }
    return 0;
  };

  const resolveCreatedAt = (file: FileRecord): string | undefined => {
    if (!file.createdAt) {
      return undefined;
    }
    const date = new Date(file.createdAt);
    return Number.isNaN(date.getTime()) ? undefined : date.toLocaleDateString('ja-JP');
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentFileIndex(index);
    setPreviewVisible(true);
  };

  const handleDownload = async (file: FileRecord) => {
    try {
      const { url } = await trpcUtils.file.getDownloadUrl.fetch({
        fileId: file.id,
        action: 'download',
      });
      const link = document.createElement('a');
      link.href = url;
      link.download = file.originalName;
      link.click();
    } catch (error) {
      messageApi.error(`ダウンロードエラー: ${getErrorMessage(error, '詳細不明')}`);
    }
  };

  const handleDelete = (file: FileRecord) => {
    let reason = '';
    modalApi.confirm({
      title: 'ファイル削除',
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <span>{`${file.originalName}を削除しますか？`}</span>
          <Input.TextArea
            placeholder="削除理由（任意）"
            autoSize={{ minRows: 2, maxRows: 4 }}
            onChange={(event) => {
              reason = event.target.value;
            }}
          />
          <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
            実ファイルの物理削除は後続バッチで処理されます。
          </span>
        </Space>
      ),
      okText: '削除',
      cancelText: 'キャンセル',
      okType: 'danger',
      onOk: () => {
        const trimmed = reason.trim();
        deleteFileMutation.mutate({ fileId: file.id, reason: trimmed.length ? trimmed : undefined });
      },
    });
  };

  const handlePrevious = () => {
    setCurrentFileIndex((prev) => (prev > 0 ? prev - 1 : files.length - 1));
  };

  const handleNext = () => {
    setCurrentFileIndex((prev) => (prev < files.length - 1 ? prev + 1 : 0));
  };

  const handleCameraCapture = (file: File) => {
    handleUpload(file);
    setCameraOpen(false);
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    accept,
    showUploadList: false,
    onDrop: (event) => {
      // 親コンテナの onDrop と二重発火して同一ファイルが2回アップロードされるのを防ぐ
      event.stopPropagation();
      setIsDragging(false);
    },
    beforeUpload: (file) => {
      handleUpload(file as File);
      return false;
    },
    disabled: files.length >= maxFiles,
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const currentFile = files[currentFileIndex];
  const canUpload = editable && files.length < maxFiles;

  return (
    <>
      <div
        onDragEnter={(e) => {
          if (canUpload) {
            e.preventDefault();
            setIsDragging(true);
          }
        }}
        onDragOver={(e) => {
          if (canUpload) {
            e.preventDefault();
          }
        }}
        onDragLeave={(e) => {
          if (canUpload && e.currentTarget === e.target) {
            setIsDragging(false);
          }
        }}
        onDrop={(e) => {
          if (canUpload) {
            e.preventDefault();
            setIsDragging(false);
            const droppedFiles = Array.from(e.dataTransfer.files);
            droppedFiles.forEach((file) => handleUpload(file));
          }
        }}
        style={{
          border: isDragging ? '2px dashed #1890ff' : 'none',
          borderRadius: 8,
          padding: isDragging ? 16 : 0,
          backgroundColor: isDragging ? 'rgba(24, 144, 255, 0.05)' : 'transparent',
          transition: 'all 0.3s',
          minHeight: isDragging ? 200 : 'auto',
        }}
      >
        {files.length === 0 && !editable ? (
          <Empty description="ファイルがありません" style={{ padding: '50px' }} />
        ) : (
          <Row gutter={[16, 16]}>
            {/* カメラ撮影カード（モバイル時のみ） */}
            {canUpload && enableCamera && isMobile && (
              <Col xs={12} sm={8} md={6} lg={4}>
                <div
                  onClick={() => setCameraOpen(true)}
                  style={{
                    cursor: 'pointer',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: `2px dashed ${token.colorBorder}`,
                    transition: 'all 0.3s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = token.colorPrimary;
                    e.currentTarget.style.backgroundColor = token.colorPrimaryBg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = token.colorBorder;
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      paddingTop: '100%',
                      position: 'relative',
                      backgroundColor: token.colorFillQuaternary,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                      }}
                    >
                      <CameraOutlined style={{ fontSize: 48, color: token.colorPrimary }} />
                      <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>カメラで撮影</Text>
                    </div>
                  </div>
                </div>
              </Col>
            )}

            {/* ファイルアップロードカード（デスクトップ時は常に表示、モバイル時はカメラがない場合のみ） */}
            {canUpload && (!isMobile || !enableCamera) && (
              <Col xs={12} sm={8} md={6} lg={4}>
                <Upload.Dragger
                  {...uploadProps}
                  style={{
                    border: `2px dashed ${token.colorBorder}`,
                    borderRadius: 8,
                    padding: 0,
                    height: '100%',
                    background: token.colorFillQuaternary,
                  }}
                  height={undefined}
                >
                  <div
                    style={{
                      width: '100%',
                      paddingTop: '100%',
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                      }}
                    >
                      <PlusOutlined style={{ fontSize: 48, color: token.colorPrimary }} />
                      <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>ファイル追加</Text>
                    </div>
                  </div>
                </Upload.Dragger>
              </Col>
            )}

            {/* 既存ファイル */}
            {files.map((file, index) => {
              const isImage = file.mimeType && file.mimeType.startsWith('image/');

              return (
                <Col key={file.id} xs={12} sm={8} md={6} lg={4}>
                  <div
                    onClick={() => handleThumbnailClick(index)}
                    style={{
                      cursor: 'pointer',
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: `1px solid ${token.colorBorder}`,
                      transition: 'all 0.3s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = token.colorPrimary;
                      e.currentTarget.style.boxShadow = token.boxShadow;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = token.colorBorder;
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        paddingTop: '100%',
                        position: 'relative',
                        backgroundColor: token.colorFillTertiary,
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {isImage && file.previewUrl ? (
                          <img
                            src={file.previewUrl}
                            alt={file.originalName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          getFileIcon(file.mimeType, file.originalName)
                        )}
                      </div>
                    </div>
                    {!isImage && (
                      <div
                        style={{
                          padding: '8px',
                          fontSize: '12px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                        }}
                      >
                        {file.originalName}
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* プレビューモーダル */}
      {currentFile && (
        <Modal
          open={previewVisible}
          title={null}
          footer={null}
          onCancel={() => setPreviewVisible(false)}
          width={900}
          centered
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000',
                position: 'relative',
              }}
            >
              {currentFile.mimeType && currentFile.mimeType.startsWith('image/') && currentFile.previewUrl ? (
                <img
                  alt={currentFile.originalName}
                  src={currentFile.previewUrl}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#fff' }}>
                  {getFileIcon(currentFile.mimeType, currentFile.originalName)}
                  <div style={{ marginTop: 16 }}>{currentFile.originalName}</div>
                </div>
              )}

              {files.length > 1 && (
                <>
                  <Button
                    type="text"
                    icon={<LeftOutlined />}
                    onClick={handlePrevious}
                    style={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#fff',
                      fontSize: 24,
                      width: 48,
                      height: 48,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    }}
                  />
                  <Button
                    type="text"
                    icon={<RightOutlined />}
                    onClick={handleNext}
                    style={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#fff',
                      fontSize: 24,
                      width: 48,
                      height: 48,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    }}
                  />
                </>
              )}
            </div>

            <div style={{ padding: 24, backgroundColor: token.colorBgContainer }}>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="ファイル名">
                  <Text copyable>{currentFile.originalName}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="ファイルサイズ">
                  {formatFileSize(resolveFileSize(currentFile))}
                </Descriptions.Item>
                <Descriptions.Item label="ファイル形式">
                  {currentFile.mimeType ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="アップロード日時">
                  {resolveCreatedAt(currentFile) ?? '-'}
                </Descriptions.Item>
                {currentFile.uploaderName && (
                  <Descriptions.Item label="アップロード者">
                    {currentFile.uploaderName}
                  </Descriptions.Item>
                )}
              </Descriptions>

              <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button icon={<DownloadOutlined />} onClick={() => handleDownload(currentFile)}>
                  ダウンロード
                </Button>
                {editable && (
                  <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(currentFile)}>
                    削除
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* カメラキャプチャモーダル */}
      <CameraCapture open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handleCameraCapture} />
    </>
  );
};
